import { useCallback, useEffect, useState } from 'react';
import { getPushPublicKey, registerDevice } from '../services/api';
import { getDeviceId, getDeviceLabel } from '../services/device';
import { getExistingSubscription, subscribeToPush } from '../services/push';
import { useNotificationPermission } from './useNotificationPermission';

export function usePushNotifications(token: string | null) {
  const { permission, isSupported, requestPermission } = useNotificationPermission();
  const [isRegistering, setIsRegistering] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    getExistingSubscription()
      .then((subscription) => setHasSubscription(Boolean(subscription)))
      .catch(() => setHasSubscription(false));
  }, []);

  const register = useCallback(async () => {
    if (!token) {
      setMessage('Please log in first.');
      return false;
    }

    if (!isSupported || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      setMessage('This browser does not support web push notifications.');
      return false;
    }

    setIsRegistering(true);
    setMessage('');

    try {
      const nextPermission = permission === 'granted' ? permission : await requestPermission();

      if (nextPermission !== 'granted') {
        setMessage('Notifications are blocked. Please enable them in browser settings.');
        return false;
      }

      const publicKey = await getPushPublicKey();
      const subscription = await subscribeToPush(publicKey);
      await registerDevice(token, getDeviceId(), getDeviceLabel(), subscription.toJSON(), nextPermission);
      setHasSubscription(true);
      setMessage('This device is ready to receive emergency alerts.');
      return true;
    } catch (error: unknown) {
      setMessage(error instanceof Error ? error.message : 'Unable to enable notifications.');
      return false;
    } finally {
      setIsRegistering(false);
    }
  }, [isSupported, permission, requestPermission, token]);

  return {
    permission,
    isSupported,
    isRegistering,
    hasSubscription,
    message,
    register,
  };
}
